import { useCallback, useEffect, useId, useMemo, useReducer, useState } from "react"
import { LoaderCircleIcon, SaveIcon, XCircleIcon, Plus, PencilIcon, TrashIcon, AlertCircle } from "lucide-react"
import { z } from "zod"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { PopoverClose } from "@radix-ui/react-popover"

export interface QuickExtensionDuration {
  months?: number
  days?: number
  hours?: number
}

export interface QuickExtensionOption {
  label: string
  duration: QuickExtensionDuration
}

export interface RetentionCaseSearchFilters {
  allowDeletedCases?: boolean
  allowLegalHold?: boolean
  allowExpiredCases?: boolean
  allowDeletionRequested?: boolean
  allowIndefinitelyRetained?: boolean
  allowExpiresBefore?: boolean
}

export interface RetentionConfig {
  showRetentionSection?: boolean
  quickExtensionOptions?: QuickExtensionOption[]
  enableDateCalendar?: boolean
  enableMDHEntry?: boolean
  caseSearchFilters?: RetentionCaseSearchFilters
}

export interface RetentionFetcherResult {
  success?: boolean
  action?: string
  errorMessage?: string
}

const DURATION_FIELDS = ["months", "days", "hours"] as const
type DurationField = (typeof DURATION_FIELDS)[number]

interface CaseSearchFilterDefinition {
  readonly key: keyof RetentionCaseSearchFilters
  readonly label: string
  readonly description: string
}

const CASE_SEARCH_FILTER_DEFINITIONS: readonly CaseSearchFilterDefinition[] = [
  {
    key: "allowDeletedCases",
    label: "Deleted Cases",
    description: "Only visible to users with Can Undelete Cases permission",
  },
  {
    key: "allowLegalHold",
    label: "Cases in Legal Hold",
    description: "All authorized users can see this filter",
  },
  {
    key: "allowExpiredCases",
    label: "Expired Cases",
    description: "Only visible if the Brand config does not allow automatic deletion",
  },
  {
    key: "allowDeletionRequested",
    label: "Cases with Deletion Requested",
    description: "Only visible if Brand config requires two-user deletion",
  },
  {
    key: "allowIndefinitelyRetained",
    label: "Indefinitely Retained Cases",
    description: "Cases with no expiry or indefinite retention",
  },
  {
    key: "allowExpiresBefore",
    label: "Cases that Expire Before",
    description: "Filter cases by expiry date with calendar picker",
  },
]

const DEFAULT_RETENTION_CONFIG: Required<RetentionConfig> = {
  showRetentionSection: false,
  quickExtensionOptions: [],
  enableDateCalendar: false,
  enableMDHEntry: false,
  caseSearchFilters: {
    allowDeletedCases: false,
    allowLegalHold: false,
    allowExpiredCases: false,
    allowDeletionRequested: false,
    allowIndefinitelyRetained: false,
    allowExpiresBefore: false,
  },
}

const durationSchema = z.object({
  months: z.number().int().nonnegative().optional(),
  days: z.number().int().nonnegative().optional(),
  hours: z.number().int().nonnegative().optional(),
})

const quickExtensionOptionSchema = z.object({
  label: z.string().trim().min(1, "Label is required"),
  duration: durationSchema.refine((duration) => Object.values(duration).some((value) => (value ?? 0) > 0), {
    message: "Please enter at least one duration value",
  }),
})

const retentionConfigSchema = z.object({
  showRetentionSection: z.boolean().optional(),
  quickExtensionOptions: z
    .array(quickExtensionOptionSchema)
    .refine(
      (options) => {
        const labels = options.map((option) => option.label.trim().toLowerCase())
        return new Set(labels).size === labels.length
      },
      { message: "Duplicate quick extension option labels are not allowed" },
    )
    .optional(),
  enableDateCalendar: z.boolean().optional(),
  enableMDHEntry: z.boolean().optional(),
  caseSearchFilters: z
    .object({
      allowDeletedCases: z.boolean().optional(),
      allowLegalHold: z.boolean().optional(),
      allowExpiredCases: z.boolean().optional(),
      allowDeletionRequested: z.boolean().optional(),
      allowIndefinitelyRetained: z.boolean().optional(),
      allowExpiresBefore: z.boolean().optional(),
    })
    .optional(),
})

function createEmptyOption(): QuickExtensionOption {
  return { label: "", duration: {} }
}

function formatDuration(duration: QuickExtensionDuration): string {
  const parts: string[] = []
  if (duration.months) parts.push(`${duration.months}m`)
  if (duration.days) parts.push(`${duration.days}d`)
  if (duration.hours) parts.push(`${duration.hours}h`)
  return parts.length > 0 ? parts.join(" ") : "—"
}

interface QuickExtensionFormProps {
  optionIndex: number
  options: QuickExtensionOption[]
  onSaveEdit: (option: QuickExtensionOption) => void
  onCancelEdit: () => void
  disabled?: boolean
}

function QuickExtensionForm({
  optionIndex,
  options,
  onSaveEdit,
  onCancelEdit,
  disabled = false,
}: QuickExtensionFormProps) {
  const [formData, setFormData] = useState<QuickExtensionOption>(() =>
    optionIndex >= options.length ? createEmptyOption() : options[optionIndex],
  )
  const [errors, setErrors] = useState<Record<string, string>>({})
  const baseId = useId()
  const formHeadingId = useId()

  const existingLabels = useMemo(
    () =>
      options.reduce((acc, option, index) => {
        if (index !== optionIndex) acc.add(option.label.trim().toLowerCase())
        return acc
      }, new Set<string>()),
    [options, optionIndex],
  )

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()

      const trimmedLabel = formData.label.trim()
      if (existingLabels.has(trimmedLabel.toLowerCase())) {
        setErrors({ label: "This label already exists" })
        return
      }

      const result = quickExtensionOptionSchema.safeParse({
        ...formData,
        label: trimmedLabel,
      })

      if (!result.success) {
        const fieldErrors: Record<string, string> = {}
        result.error.issues.forEach((issue) => {
          const path = issue.path.join(".")
          fieldErrors[path] = issue.message
        })
        setErrors(fieldErrors)
        return
      }

      setErrors({})
      onSaveEdit(result.data)
    },
    [formData, existingLabels, onSaveEdit],
  )

  const handleDurationChange = useCallback((field: DurationField, value: string) => {
    const parsed = value === "" ? undefined : Math.max(0, Math.trunc(Number(value)) || 0)
    setFormData((prev) => ({
      ...prev,
      duration: { ...prev.duration, [field]: parsed },
    }))
  }, [])

  return (
    <form
      onSubmit={handleSubmit}
      noValidate
      aria-labelledby={formHeadingId}
      className="space-y-4 rounded-lg border p-4"
    >
      <h4 id={formHeadingId} className="sr-only">
        {optionIndex >= options.length ? "Add quick extension option" : "Edit quick extension option"}
      </h4>

      <div>
        <label htmlFor={`${baseId}-label`} className="block text-sm font-medium text-foreground">
          Label
        </label>
        <Input
          id={`${baseId}-label`}
          value={formData.label}
          onChange={(event) => setFormData((prev) => ({ ...prev, label: event.target.value }))}
          placeholder="e.g., Extend Case Retention by 2 Years"
          aria-invalid={Boolean(errors.label)}
          aria-describedby={errors.label ? `${baseId}-label-error` : undefined}
          className={errors.label ? "border-red-500 dark:border-red-500" : ""}
          disabled={disabled}
        />
        {errors.label && (
          <p id={`${baseId}-label-error`} role="alert" className="mt-1 text-xs text-red-600 dark:text-red-400">
            {errors.label}
          </p>
        )}
      </div>

      <fieldset disabled={disabled}>
        <legend className="mb-3 block text-sm font-medium text-foreground">Duration</legend>
        <div className="grid grid-cols-4 gap-2">
          {DURATION_FIELDS.map((field) => {
            const fieldId = `${baseId}-${field}`
            return (
              <div key={field}>
                <label htmlFor={fieldId} className="text-xs text-muted-foreground capitalize">
                  {field}
                </label>
                <Input
                  id={fieldId}
                  type="number"
                  min={0}
                  step={1}
                  value={formData.duration[field] ?? ""}
                  onChange={(event) => handleDurationChange(field, event.target.value)}
                  placeholder="0"
                  aria-label={`Duration in ${field}`}
                  aria-describedby={errors.duration ? `${baseId}-duration-error` : undefined}
                />
              </div>
            )
          })}
        </div>
        {errors.duration && (
          <p id={`${baseId}-duration-error`} role="alert" className="mt-1 text-xs text-red-600">
            {errors.duration}
          </p>
        )}
      </fieldset>

      <div className="flex gap-2">
        <Button type="submit" disabled={disabled} className="flex-1 bg-info hover:bg-info/90 disabled:opacity-50">
          {optionIndex >= options.length ? "Add" : "Update"}
        </Button>
        <Button type="button" variant="outline" className="flex-1" onClick={onCancelEdit} disabled={disabled}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

interface QuickExtensionListProps extends Omit<QuickExtensionFormProps, "optionIndex"> {
  editingOptionIndex: number | null
  onDelete: (index: number) => void
  onStartEdit: (index: number) => void
}

function QuickExtensionList({
  options,
  editingOptionIndex,
  onDelete,
  onSaveEdit,
  onStartEdit,
  onCancelEdit,
  disabled = false,
}: QuickExtensionListProps) {
  if (options.length === 0) {
    return (
      <div className="rounded-lg border border-dashed bg-primary-foreground p-6 text-center">
        <p className="text-sm text-muted-foreground">No quick extension options configured yet</p>
      </div>
    )
  }

  return (
    <ul className="space-y-2">
      {options.map((option, index) => {
        const key = `${index}-${option.label}`

        if (index === editingOptionIndex) {
          return (
            <QuickExtensionForm
              key={key}
              options={options}
              optionIndex={index}
              onSaveEdit={onSaveEdit}
              onCancelEdit={onCancelEdit}
              disabled={disabled}
            />
          )
        }

        return (
          <li key={key} className="flex items-center justify-between rounded-lg border p-4">
            <div className="flex items-center gap-3">
              <span
                aria-hidden="true"
                className="flex h-6 w-6 items-center justify-center rounded-full bg-info/15 text-xs font-medium text-info"
              >
                {index + 1}
              </span>
              <div>
                <p className="font-medium text-foreground">{option.label}</p>
                <p className="text-sm text-muted-foreground">{formatDuration(option.duration)}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-info hover:bg-info/10"
                disabled={editingOptionIndex !== null || disabled}
                onClick={() => onStartEdit(index)}
                aria-label={`Edit option: ${option.label}`}
              >
                <PencilIcon className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-red-600 hover:bg-red-50"
                disabled={editingOptionIndex !== null || disabled}
                onClick={() => onDelete(index)}
                aria-label={`Delete option: ${option.label}`}
              >
                <TrashIcon className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

interface ToggleSettingCardProps {
  title: string
  description: string
  checked: boolean
  onCheckedChange: () => void
  disabled?: boolean
}

function ToggleSettingCard({ title, description, checked, onCheckedChange, disabled = false }: ToggleSettingCardProps) {
  const inputId = useId()
  const descriptionId = useId()
  return (
    <section className="rounded-lg border p-4">
      <div className="flex items-center justify-between">
        <label htmlFor={inputId} className="flex cursor-pointer items-center gap-3">
          <Checkbox
            id={inputId}
            checked={checked}
            onCheckedChange={onCheckedChange}
            aria-label={title}
            aria-describedby={descriptionId}
            disabled={disabled}
          />
          <div>
            <h3 className="text-lg font-medium text-foreground">{title}</h3>
            <p id={descriptionId} className="mt-1 text-sm text-muted-foreground">
              {description}
            </p>
          </div>
        </label>
      </div>
    </section>
  )
}

export interface SharedRetentionTabProps {
  config: RetentionConfig
  onSave: (config: RetentionConfig) => Promise<void> | void
  onRemove: () => Promise<void> | void
  isFetching: boolean
  currentAction?: "SAVE_SECTION" | "REMOVE_SECTION" | null
  fetcherData?: RetentionFetcherResult
}

export function SharedRetentionTab({
  config,
  onSave,
  onRemove,
  isFetching,
  currentAction,
  fetcherData,
}: SharedRetentionTabProps) {
  const quickExtensionHeadingId = useId()
  const quickExtensionDescriptionId = useId()
  const caseSearchFilterHeadingId = useId()
  const caseSearchFilterDescriptionId = useId()

  const [retentionState, setRetentionState] = useReducer(
    (
      state: { config: Required<RetentionConfig>; editingExtensionOptionIndex: number | null },
      action:
        | { type: "SAVE_EXTENSION_OPTION"; option: QuickExtensionOption }
        | { type: "DELETE_EXTENSION_OPTION"; index: number }
        | { type: "TOGGLE_RETENTION_SECTION" }
        | { type: "TOGGLE_DATE_CALENDAR" }
        | { type: "TOGGLE_MDH_ENTRY" }
        | { type: "TOGGLE_CASE_SEARCH_FILTER"; field: keyof RetentionCaseSearchFilters }
        | { type: "SET_EDITING_EXTENSION_OPTION_INDEX"; index: number | null }
        | { type: "RESET" },
    ) => {
      if (action.type === "RESET")
        return { editingExtensionOptionIndex: null, config: structuredClone(DEFAULT_RETENTION_CONFIG) }

      if (action.type === "SET_EDITING_EXTENSION_OPTION_INDEX") {
        return { ...state, editingExtensionOptionIndex: action.index }
      }

      if (action.type === "TOGGLE_RETENTION_SECTION") {
        return { ...state, config: { ...state.config, showRetentionSection: !state.config.showRetentionSection } }
      }

      if (action.type === "TOGGLE_DATE_CALENDAR") {
        return { ...state, config: { ...state.config, enableDateCalendar: !state.config.enableDateCalendar } }
      }

      if (action.type === "TOGGLE_MDH_ENTRY") {
        return { ...state, config: { ...state.config, enableMDHEntry: !state.config.enableMDHEntry } }
      }

      if (action.type === "TOGGLE_CASE_SEARCH_FILTER") {
        const field = action.field
        return {
          ...state,
          config: {
            ...state.config,
            caseSearchFilters: {
              ...state.config.caseSearchFilters,
              [field]: !(state.config.caseSearchFilters[field] ?? false),
            },
          },
        }
      }

      if (action.type === "DELETE_EXTENSION_OPTION") {
        return {
          ...state,
          config: {
            ...state.config,
            quickExtensionOptions: state.config.quickExtensionOptions.filter((_, i) => i !== action.index),
          },
        }
      }

      if (action.type === "SAVE_EXTENSION_OPTION" && state.editingExtensionOptionIndex != null) {
        return {
          ...state,
          config: {
            ...state.config,
            quickExtensionOptions:
              state.editingExtensionOptionIndex >= state.config.quickExtensionOptions.length
                ? [...state.config.quickExtensionOptions, action.option]
                : state.config.quickExtensionOptions.map((_, i) =>
                    i === state.editingExtensionOptionIndex ? action.option : _,
                  ),
          },
          editingExtensionOptionIndex: null,
        }
      }

      return state
    },
    undefined,
    () => ({
      editingExtensionOptionIndex: null,
      config: structuredClone({
        showRetentionSection: config.showRetentionSection ?? DEFAULT_RETENTION_CONFIG.showRetentionSection,
        quickExtensionOptions: config.quickExtensionOptions ?? DEFAULT_RETENTION_CONFIG.quickExtensionOptions,
        enableDateCalendar: config.enableDateCalendar ?? DEFAULT_RETENTION_CONFIG.enableDateCalendar,
        enableMDHEntry: config.enableMDHEntry ?? DEFAULT_RETENTION_CONFIG.enableMDHEntry,
        caseSearchFilters: { ...DEFAULT_RETENTION_CONFIG.caseSearchFilters, ...config.caseSearchFilters },
      }),
    }),
  )

  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (!fetcherData) return

    if (fetcherData.success && fetcherData.action === "REMOVE_SECTION") {
      setRetentionState({ type: "RESET" })
      setFormError(null)
      return
    }

    if (fetcherData.success && fetcherData.action === "SAVE_SECTION") {
      setFormError(null)
      setRetentionState({ type: "SET_EDITING_EXTENSION_OPTION_INDEX", index: null })
      return
    }

    if (fetcherData.success === false && fetcherData.errorMessage) {
      setFormError(fetcherData.errorMessage)
    }
  }, [fetcherData])

  const handleSave = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      setFormError(null)

      const result = retentionConfigSchema.safeParse(retentionState.config satisfies RetentionConfig)
      if (!result.success) {
        const messages = result.error.issues.map((issue) => issue.message)
        setFormError(messages.join(" "))
        return
      }

      onSave(result.data)
    },
    [retentionState.config, onSave],
  )

  const handleRemoveSection = useCallback(() => {
    onRemove()
  }, [onRemove])

  const sectionDisabled = !retentionState.config.showRetentionSection
  const showSaveLoading = isFetching && (currentAction === "SAVE_SECTION" || !currentAction)
  const showRemoveLoading = isFetching && currentAction === "REMOVE_SECTION"

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-full flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="mx-auto w-full space-y-8">
            {formError && (
              <div
                role="alert"
                aria-live="assertive"
                className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <p>{formError}</p>
              </div>
            )}

            <ToggleSettingCard
              title="Retention Configuration"
              description="Tick to enable retention settings. Untick to disable editing."
              checked={retentionState.config.showRetentionSection}
              onCheckedChange={() => setRetentionState({ type: "TOGGLE_RETENTION_SECTION" })}
            />

            <section
              aria-labelledby={quickExtensionHeadingId}
              aria-describedby={quickExtensionDescriptionId}
              className={`rounded-lg border p-4 transition-opacity ${sectionDisabled ? "pointer-events-none opacity-50" : ""}`}
            >
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 id={quickExtensionHeadingId} className="text-lg font-medium text-foreground">
                    Case Retention Quick Extension Options
                  </h3>
                  <p id={quickExtensionDescriptionId} className="mt-1 text-sm text-muted-foreground">
                    Configure quick extension buttons that appear in Viewer for authorized users
                  </p>
                </div>
                {retentionState.editingExtensionOptionIndex === null && !sectionDisabled && (
                  <Button
                    type="button"
                    aria-label="Add quick extension option"
                    className="bg-info hover:bg-info/90 disabled:opacity-50"
                    onClick={() =>
                      setRetentionState({
                        type: "SET_EDITING_EXTENSION_OPTION_INDEX",
                        index: retentionState.config.quickExtensionOptions.length,
                      })
                    }
                  >
                    <Plus className="h-4 w-4" aria-hidden="true" />
                    <span className="hidden sm:inline" aria-hidden="true">
                      Add
                    </span>
                  </Button>
                )}
              </div>

              <div className="space-y-4">
                {retentionState.editingExtensionOptionIndex !== null &&
                  retentionState.editingExtensionOptionIndex >= retentionState.config.quickExtensionOptions.length && (
                    <QuickExtensionForm
                      options={retentionState.config.quickExtensionOptions}
                      optionIndex={retentionState.editingExtensionOptionIndex}
                      onSaveEdit={(option) => setRetentionState({ type: "SAVE_EXTENSION_OPTION", option })}
                      onCancelEdit={() =>
                        setRetentionState({ type: "SET_EDITING_EXTENSION_OPTION_INDEX", index: null })
                      }
                      disabled={sectionDisabled}
                    />
                  )}
                <QuickExtensionList
                  options={retentionState.config.quickExtensionOptions}
                  editingOptionIndex={retentionState.editingExtensionOptionIndex}
                  onDelete={(index) => setRetentionState({ type: "DELETE_EXTENSION_OPTION", index })}
                  onSaveEdit={(option) => setRetentionState({ type: "SAVE_EXTENSION_OPTION", option })}
                  onStartEdit={(index) => setRetentionState({ type: "SET_EDITING_EXTENSION_OPTION_INDEX", index })}
                  onCancelEdit={() => setRetentionState({ type: "SET_EDITING_EXTENSION_OPTION_INDEX", index: null })}
                  disabled={sectionDisabled}
                />
              </div>
            </section>

            <div className={`transition-opacity ${sectionDisabled ? "pointer-events-none opacity-50" : ""}`}>
              <ToggleSettingCard
                title="Date/Calendar Control"
                description="Allow users to select a new case expiry date"
                checked={retentionState.config.enableDateCalendar}
                onCheckedChange={() => setRetentionState({ type: "TOGGLE_DATE_CALENDAR" })}
                disabled={sectionDisabled}
              />
            </div>

            <div className={`transition-opacity ${sectionDisabled ? "pointer-events-none opacity-50" : ""}`}>
              <ToggleSettingCard
                title="Manual Duration Entry"
                description="Allow users to manually enter months, days, and hours for retention duration"
                checked={retentionState.config.enableMDHEntry}
                onCheckedChange={() => setRetentionState({ type: "TOGGLE_MDH_ENTRY" })}
                disabled={sectionDisabled}
              />
            </div>

            <section
              className={`rounded-lg border p-4 transition-opacity ${sectionDisabled ? "pointer-events-none opacity-50" : ""}`}
              aria-labelledby={caseSearchFilterHeadingId}
              aria-describedby={caseSearchFilterDescriptionId}
            >
              <h3 id={caseSearchFilterHeadingId} className="mb-4 text-lg font-medium text-foreground">
                Case Search Filter Availability
              </h3>
              <p id={caseSearchFilterDescriptionId} className="mb-4 text-sm text-muted-foreground">
                Configure which retention status filters appear in Viewer Case Search
              </p>

              <div className="space-y-3">
                {CASE_SEARCH_FILTER_DEFINITIONS.map((filter) => {
                  const inputId = `filter-${filter.key}`
                  const descriptionId = `${inputId}-description`
                  return (
                    <div
                      key={filter.key}
                      className={`rounded-lg border p-3 transition-colors ${
                        sectionDisabled ? "bg-primary-foreground" : "hover:bg-info/10"
                      }`}
                    >
                      <label className="flex cursor-pointer items-center gap-3">
                        <Checkbox
                          id={inputId}
                          checked={retentionState.config.caseSearchFilters?.[filter.key] ?? false}
                          onCheckedChange={() =>
                            setRetentionState({ type: "TOGGLE_CASE_SEARCH_FILTER", field: filter.key })
                          }
                          aria-label={filter.label}
                          aria-describedby={descriptionId}
                          disabled={sectionDisabled}
                        />
                        <div className="flex-1">
                          <p className="font-medium text-foreground">{filter.label}</p>
                          <p id={descriptionId} className="text-xs text-muted-foreground">
                            {filter.description}
                          </p>
                        </div>
                      </label>
                    </div>
                  )
                })}
              </div>
            </section>
          </div>
        </div>

        <form onSubmit={handleSave} className="flex-shrink-0 border-t p-4">
          <div className="mx-auto flex w-full items-center gap-3">
            <Button type="submit" disabled={isFetching} className="flex-1 bg-info hover:bg-info/90 disabled:opacity-50">
              {showSaveLoading && <LoaderCircleIcon className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
              <SaveIcon className="h-4 w-4" aria-hidden="true" />
              Save Retention Configuration
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isFetching}
                  aria-label="Remove retention configuration section"
                  className="flex-1 gap-2 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                >
                  {showRemoveLoading ? (
                    <LoaderCircleIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <XCircleIcon className="h-4 w-4" aria-hidden="true" />
                  )}
                  <span className="hidden sm:inline" aria-hidden="true">
                    Remove Section
                  </span>
                  <span className="sm:hidden" aria-hidden="true">
                    Remove
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent side="top" className="w-80" role="alertdialog" aria-label="Confirm removal">
                <div className="space-y-3">
                  <p className="text-sm font-medium">Remove Retention Configuration?</p>
                  <p className="text-sm text-muted-foreground">
                    This will remove all retention configurations and reset to default values.
                  </p>
                  <div className="flex items-center justify-end gap-3">
                    <PopoverClose asChild>
                      <Button type="button" variant="ghost" size="sm">
                        Cancel
                      </Button>
                    </PopoverClose>
                    <PopoverClose asChild>
                      <Button type="button" variant="destructive" size="sm" onClick={handleRemoveSection}>
                        Remove
                      </Button>
                    </PopoverClose>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </form>
      </div>
    </div>
  )
}
